import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

const BeardPatterns = () => {
  const data = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  
  // Only render if beardPatterns exist (bart variant)
  if (!data.beardPatterns || !data.features?.showBeardPatterns) {
    return null;
  }

  const { beardPatterns } = data;

  // Translations for beard pattern titles and descriptions
  const patternTranslations = {
    'Kompletter Bart': { en: 'Complete Beard' },
    'Dünner Bart': { en: 'Thin Beard' },
    'Bartkontur': { en: 'Beard Contour' },
    'Bart mit Lücken': { en: 'Beard with Gaps' },
    'Lückenhafter Bart': { en: 'Patchy Beard' },
    'Kinnbart-Lücken': { en: 'Chin Beard Gaps' },
    'Schwacher Bartwuchs': { en: 'Weak Beard Growth' },
    'Narben im Bartbereich': { en: 'Scars in Beard Area' },
  };

  const descriptionTranslations = {
    'Umfassende Behandlung für einen vollständigen Bart mit optimaler Dichte': {
      en: 'Comprehensive treatment for a complete beard with optimal density'
    },
    'Behandlung für spärlichen oder dünnen Bartwuchs zur Erhöhung der Dichte': {
      en: 'Treatment for sparse or thin beard growth to increase density'
    },
    'Präzise Definition der Bartlinien und Konturen': {
      en: 'Precise definition of beard lines and contours'
    },
    'Gezielte Behandlung von lückenhaften Bereichen im Bartwuchs': {
      en: 'Targeted treatment of patchy areas in beard growth'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return patternTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };

  return (
    <section id="bartmuster" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 left-0 w-[400px] h-[400px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {language === 'de' ? 'Behandlungsoptionen' : 'Treatment Options'}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Typische ' : 'Typical '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Bartmuster' : 'Beard Patterns'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Jedes Bartmuster erfordert einen individuellen Behandlungsansatz. Hier finden Sie einen Überblick über die häufigsten Muster und die entsprechenden Behandlungsoptionen.'
              : 'Each beard pattern requires an individual treatment approach. Here you will find an overview of the most common patterns and corresponding treatment options.'}
          </p>
        </div>

        {/* Beard Patterns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {beardPatterns.map((pattern) => (
            <div
              key={pattern.id}
              data-testid={`beard-pattern-${pattern.id}`}
              className={`group relative rounded-2xl overflow-hidden border transition-all duration-500 flex flex-col card-lift ${
                isDark 
                  ? 'bg-[#0f0f0f] border-white/5 hover:border-[#7BA7C2]/30'
                  : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
              }`}
            >
              {/* Image - larger height */}
              <div className="relative h-64 sm:h-72 lg:h-64 overflow-hidden flex-shrink-0">
                <img
                  src={pattern.image}
                  alt={`${getTranslatedTitle(pattern.title)} Barttransplantation Muster - Dion Hair Clinic`}
                  loading="lazy"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent`} />
                
                {/* Title overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-[#9BBFD9] transition-colors duration-300">
                    {getTranslatedTitle(pattern.title)}
                  </h3>
                </div>
              </div>

              {/* Content - flex-grow ensures equal height */}
              <div className="p-6 flex flex-col flex-grow">
                <p className={`text-sm mb-4 min-h-[4.5rem] lg:min-h-[5rem] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {getTranslatedDescription(pattern.description)}
                </p>
                
                {/* Stats - pushed to bottom with mt-auto */}
                <div className="space-y-3 mt-auto">
                  <div className={`flex justify-between items-center py-2 border-t ${
                    isDark ? 'border-white/5' : 'border-gray-100'
                  }`}>
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {language === 'de' ? 'Typische Grafts' : 'Typical Grafts'}
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                      {pattern.grafts}
                    </span>
                  </div>
                  <div className={`flex justify-between items-center py-2 border-t ${
                    isDark ? 'border-white/5' : 'border-gray-100'
                  }`}>
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {language === 'de' ? 'Behandlung' : 'Treatment'}
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'de' ? pattern.sessions : pattern.sessions.replace('Sitzung', 'Session').replace('Sitzungen', 'Sessions')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeardPatterns;
